import type { apiPatientPortalContract } from '@montugroup/pms-api-contracts';
import { DateTime } from 'luxon';
import type { FieldValues } from 'react-hook-form';
import type { z } from 'zod';

import { PRESCREENING_FORM_EMAIL_LOCAL_STORAGE_KEY } from '@/pages/PreScreeningForms/PreScreeningForm';
import { getBrowserTimezone } from '@/utils/timezone';
import { getSearchParams } from '@/utils/url';

type SubmitD2DPreConsultationFormPostBody = z.infer<typeof apiPatientPortalContract.submitD2DPreConsultationForm.body>;

type D2DAnswer = SubmitD2DPreConsultationFormPostBody['data']['answers'][number];

export const handleDirectToDoctorAnswersTransform = (formValues: FieldValues): SubmitD2DPreConsultationFormPostBody => {
  const email = getSearchParams('email', {
    localStorageKey: PRESCREENING_FORM_EMAIL_LOCAL_STORAGE_KEY,
  })?.trim();

  const { mobileNumber, fullName, birthday, address, emergencyContact, medicare, gender } = formValues;

  const answers = [
    mobileNumber?.phoneNo && {
      questionType: 'd2d-mobile',
      mobileNumber: mobileNumber.phoneNo,
    },
    fullName?.firstName &&
      fullName?.lastName && {
        questionType: 'd2d-name',
        firstName: fullName.firstName,
        lastName: fullName.lastName,
      },
    birthday && {
      questionType: 'd2d-dob',
      birthday: DateTime.fromISO(birthday).toISODate()!,
      timezone: getBrowserTimezone() ?? 'UTC+10',
    },
    address?.streetAndHouseNo && {
      questionType: 'd2d-address',
      streetAndHouseNo: address.streetAndHouseNo,
      suburb: address.suburb,
      state: address.state,
      postcode: address.postcode,
    },
    emergencyContact?.phoneNo && {
      questionType: 'd2d-emergency-contact',
      fullName: `${emergencyContact.firstName ?? ''} ${emergencyContact.lastName ?? ''}`.trim(),
      relationship: emergencyContact.relationship,
      phoneNo: emergencyContact.phoneNo,
    },
    medicare?.number && {
      questionType: 'd2d-medicare',
      number: medicare.number,
      referenceNo: medicare.referenceNo,
      fullName: medicare.fullName,
      expiryDate: DateTime.fromISO(medicare.expiryDate).toISODate()!,
    },
    gender && {
      questionType: 'd2d-gender',
      gender,
    },
  ].filter(Boolean) as D2DAnswer[];

  return {
    data: {
      email: email ?? '',
      answers: answers as SubmitD2DPreConsultationFormPostBody['data']['answers'],
    },
  };
};
